import { useContext, useEffect, useState } from 'react';
import { Configure, InstantSearch, useHits } from 'react-instantsearch';
import { useIntl } from 'react-intl';

import { useAlgoliaUserToken } from '@/hooks/useAlgoliaUserToken';
import { getIndexName } from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { StoreHit } from '@/integrations/algolia/types';
import { commonMessages } from '@/messages/common';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { LocaleContext } from '@/providers/LocaleProvider';
import { listingActions } from '@/states/listingState';

import type { ExhibitionItem } from '../../ExhibitionList';
import type { UpcomingExhibitionItemType } from '../UpcomingExhibitionItem/UpcomingExhibitionItem';
import { UpcomingExhibitionItem } from '../UpcomingExhibitionItem/UpcomingExhibitionItem';

import * as S from '../../ExhibitionList.styles';

const loadMoreNumber = 5;

export const UpcomingExhibitionItemsContent = ({
  loadMoreLabel,
  upcomingExhibitions,
}: {
  loadMoreLabel: string | null | undefined;
  upcomingExhibitions: ExhibitionItem[];
}) => {
  const { formatMessage } = useIntl();

  const [visibleCount, setVisibleCount] = useState(loadMoreNumber);
  const { hits } = useHits<StoreHit>();
  const handleLoadMore = () => {
    setVisibleCount((prevCount) => prevCount + loadMoreNumber);
  };
  const results = upcomingExhibitions?.map((item) => {
    const matchingHit = hits.find((hit) => hit.store_id === item.boutiqueId);
    return {
      ...item,
      ...matchingHit,
    };
  });
  return (
    <>
      {results?.slice(0, visibleCount).map((boutique, index: number) => {
        const imagePosition = index % 2 === 0 ? 'left' : 'right';
        return (
          <UpcomingExhibitionItem
            key={boutique.boutiqueId}
            boutique={boutique as UpcomingExhibitionItemType['boutique']}
            imagePosition={imagePosition}
          />
        );
      })}
      {!!results?.length &&
        results?.length > 0 &&
        visibleCount < results.length && (
          <S.StyledContainer>
            <S.LoadMoreCtaOuter>
              <S.LoadMoreButton onClick={handleLoadMore}>
                {loadMoreLabel ?? formatMessage(commonMessages.loadingMore)}
              </S.LoadMoreButton>
            </S.LoadMoreCtaOuter>
          </S.StyledContainer>
        )}
    </>
  );
};

export const UpcomingExhibitionItemsContainer = ({
  loadMoreLabel,
  upcomingExhibitions,
}: {
  loadMoreLabel: string | null | undefined;
  upcomingExhibitions: ExhibitionItem[];
}) => {
  const draftMode = useContext(DraftModeContext);
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const locale = useContext(LocaleContext);
  const { userToken } = useAlgoliaUserToken();
  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
  });
  const indexName = getIndexName('STORE', locale);
  useEffect(() => {
    listingActions.updateListingType('STORE');
  }, []);
  useEffect(() => {
    if (upcomingExhibitions && upcomingExhibitions?.length > 0) {
      const boutiqueIds = upcomingExhibitions?.map((item) => item?.boutiqueId);
      const transformedIds = boutiqueIds.map((id) => `store_id:${id}`);
      setStoreIds(transformedIds);
    }
  }, [upcomingExhibitions]);

  return (
    <InstantSearch
      indexName={indexName}
      insights={{ insightsInitParams: { userToken } as any }}
      searchClient={algoliaSearchClient}
    >
      <Configure facetFilters={[[...storeIds], 'web_visible:true']} />
      <UpcomingExhibitionItemsContent
        loadMoreLabel={loadMoreLabel}
        upcomingExhibitions={upcomingExhibitions}
      />
    </InstantSearch>
  );
};
